import React, { useEffect, useState } from "react";
import {
    Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
    FormControl, FormControlLabel, FormHelperText, Grid, InputLabel, MenuItem, Select,
    Switch, TextField, Tooltip, Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import Swal from "sweetalert2";
import { useSnackbar } from "notistack";
import companyServices from "app/services/company-services";
import centreServices from "app/services/centre-services";
import vehicleServices from "app/services/vehicle-services";

// Configuración de ruteo, en dos niveles.
//
// La empresa define cómo quiere rutear y el vehículo pisa lo que en él es distinto. Lo que el
// vehículo no dice, lo hereda.
//
// No todos los campos tienen sentido en los dos niveles, y por eso cada uno declara dónde se
// muestra. Lo que es del negocio —el motor, la jornada, el tiempo por parada, el costo del
// chofer— se decide una vez para la empresa. Lo que cambia de camión en camión —a dónde vuelve,
// cuánto consume, si esquiva peajes— se decide en el vehículo.
//
// `soloEmpresa`: no se muestra en la ficha del vehículo.
// `soloVehiculo`: no se muestra en la configuración de la empresa.
// `fueraDeSettings`: no vive en el jsonb `routing_settings` sino en una columna del vehículo.
const CAMPOS = [
    {
        grupo: "Motor y forma de la ruta",
        items: [
            {
                key: "toll_cost_enabled", tipo: "switch", label: "Incluir peajes en el costo",
                help: "Vienen en la misma respuesta que la ruta: no cuestan una transacción extra.",
            },
            {
                key: "avoid_tolls", tipo: "switch", label: "Evitar peajes y tag",
                soloVehiculo: true, fueraDeSettings: true,
                help: "Las rutas se calculan esquivando autopistas con peaje y pórticos con tag. Al ordenar las paradas el peaje solo se penaliza: si no hay alternativa, la ruta puede pasar igual.",
            },
            {
                key: "return_to_depot", tipo: "switch", label: "El vehículo vuelve a la bodega",
                help: "Con la ruta abierta el regreso no entra en la optimización, y el óptimo termina siempre en la parada más lejana.",
            },
            {
                key: "return_centre_id", tipo: "bodega", label: "Vuelve a",
                dependeDe: "return_to_depot",
                help: "Para el camión que carga en una bodega y termina la jornada en otra. Vacío = vuelve a la bodega desde la que salió.",
            },
            {
                key: "sequencer", tipo: "select", label: "Motor de secuenciación", soloEmpresa: true,
                opciones: [
                    { value: "here", label: "HERE (findsequence2)" },
                    { value: "local", label: "Propio (matriz + óptimo exacto)" },
                ],
                help: "El propio resuelve el óptimo exacto hasta 14 paradas y permite excluir peajes de verdad, no solo penalizarlos.",
            },
            {
                key: "objective", tipo: "select", label: "Optimizar por", soloEmpresa: true,
                opciones: [
                    { value: "time", label: "Tiempo" },
                    { value: "distance", label: "Distancia" },
                ],
                help: "En una ruta cerrada los dos sentidos cuestan igual en distancia: esto rompe el empate.",
            },
            {
                key: "reoptimize_from_gps", tipo: "switch", label: "Recalcular desde la posición del camión",
                soloEmpresa: true,
                help: "Con el viaje en curso, reoptimiza desde donde va el camión y no desde la bodega.",
            },
        ],
    },
    {
        grupo: "Tiempos y jornada",
        items: [
            {
                key: "service_time_mode", tipo: "select", label: "Tiempo en cada parada", soloEmpresa: true,
                opciones: [
                    { value: "fixed", label: "Fijo" },
                    { value: "learned", label: "Aprendido por dirección" },
                ],
                help: "El aprendido usa la mediana histórica de esa misma dirección. Necesita 3 entregas cerradas; si no las hay, usa el fijo.",
            },
            { key: "default_service_time_min", tipo: "number", label: "Minutos por parada (fijo)", soloEmpresa: true },
            { key: "workday_start", tipo: "hora", label: "Jornada desde", placeholder: "08:30", soloEmpresa: true },
            { key: "workday_end", tipo: "hora", label: "Jornada hasta", placeholder: "18:00", soloEmpresa: true },
            {
                key: "enforce_workday", tipo: "switch", label: "Marcar entregas fuera de jornada", soloEmpresa: true,
                help: "No las saca del viaje: las marca para que el despachador decida.",
            },
            {
                key: "max_deliveries_per_trip", tipo: "number", label: "Tope de entregas por viaje", soloEmpresa: true,
                help: "Vacío = sin tope.",
            },
        ],
    },
    {
        grupo: "Energía",
        help: "No hace falta calcular el costo por kilómetro: se saca del precio y del rendimiento. Cuando suba el diésel —o la luz— cambias un número.",
        items: [
            {
                key: "energy_type", tipo: "select", label: "Con qué se mueve",
                opciones: [
                    { value: "diesel", label: "Diésel" },
                    { value: "gasoline", label: "Bencina" },
                    { value: "electric", label: "Eléctrico" },
                ],
                help: "En un eléctrico el costo sale de los kWh que mide HERE en la misma ruta, no de un promedio por kilómetro.",
            },
            {
                key: "fuel_price_per_liter", tipo: "number", label: "Precio del litro",
                visibleSi: (f) => f.energy_type !== "electric",
                help: "Diésel, ~$1.330 a septiembre de 2026.",
            },
            {
                key: "fuel_km_per_liter", tipo: "number", label: "Rendimiento (km por litro)",
                visibleSi: (f) => f.energy_type !== "electric",
                help: "Furgón ~9 · Camión ¾ ~6 · Tracto ~3.",
            },
            {
                key: "energy_price_per_kwh", tipo: "number", label: "Precio del kWh",
                visibleSi: (f) => f.energy_type === "electric",
                help: "Cargando en bodega es la tarifa comercial; en carga rápida pública es bastante más.",
            },
            {
                key: "energy_kwh_per_100km", tipo: "number", label: "Consumo (kWh por 100 km)",
                visibleSi: (f) => f.energy_type === "electric",
                help: "El de la ficha técnica. Furgón eléctrico ~30-40. Le dice a HERE con qué vehículo trata; el consumo real lo calcula HERE con la pendiente y el tráfico.",
            },
            {
                key: "usable_battery_kwh", tipo: "number", label: "Batería útil (kWh)",
                visibleSi: (f) => f.energy_type === "electric",
                help: "La que el fabricante deja usar de verdad, no la nominal. Con esto se avisa si el viaje no cabe.",
            },
            {
                key: "range_reserve_pct", tipo: "number", label: "Reserva de batería (%)",
                visibleSi: (f) => f.energy_type === "electric",
                help: "Margen que no se toca al planificar: cubre frío, carga y desvíos. 15% es razonable.",
            },
            {
                key: "auxiliary_kw", tipo: "number", label: "Accesorios (kW)",
                visibleSi: (f) => f.energy_type === "electric",
                help: "Clima, luces y electrónica. HERE los cobra por hora de viaje, y en reparto urbano llegan a ser el 40% del consumo. Vacío = 1,8.",
            },
            { key: "fuel_extra_pct", tipo: "number", label: "Extra por mantención (%)", help: "Neumáticos, mantención y depreciación. Suele ir entre 40% y 50%." },
        ],
    },
    {
        grupo: "Chofer",
        help: "Se ingresa el sueldo líquido, que es el dato que uno tiene a mano, y el costo por hora se calcula.",
        items: [
            { key: "driver_net_salary", tipo: "number", label: "Sueldo líquido mensual", soloEmpresa: true },
            {
                key: "driver_cost_factor", tipo: "number", label: "Factor de costo empresa", soloEmpresa: true,
                help: "Leyes sociales, cesantía, mutual, gratificación y vacaciones. 1,40 es lo habitual.",
            },
            {
                key: "driver_hours_per_month", tipo: "number", label: "Horas al mes", soloEmpresa: true,
                help: "173,3 con jornada de 40 horas · 182 con la de 42, que es la vigente.",
            },
        ],
    },
];

// Las mismas fórmulas que el backend, solo para previsualizar mientras se escribe.
// El valor que manda es el que calcula Routing::Settings al rutear.
const costoCombustible = (f) => {
    const extraPct = Number(f.fuel_extra_pct) || 0;

    // En eléctrico esto es el RESPALDO, no el costo real: sirve cuando HERE no
    // devuelve consumo. El número que se usa al rutear sale de los kWh medidos.
    if (f.energy_type === "electric") {
        const kwh = Number(f.energy_price_per_kwh);
        const cien = Number(f.energy_kwh_per_100km);
        if (!kwh || !cien) return null;

        return Math.round(kwh * (cien / 100) * (1 + extraPct / 100));
    }

    const litro = Number(f.fuel_price_per_liter);
    const rendimiento = Number(f.fuel_km_per_liter);
    if (!litro || !rendimiento) return null;

    const extra = Number(f.fuel_extra_pct) || 0;
    return Math.round((litro / rendimiento) * (1 + extra / 100));
};

const costoChofer = (f) => {
    const liquido = Number(f.driver_net_salary);
    if (!liquido) return null;

    const factor = Number(f.driver_cost_factor) || 1.4;
    const horas = Number(f.driver_hours_per_month) || 173.33;
    if (!horas) return null;

    return Math.round((liquido * factor) / horas);
};

const TODOS_LOS_CAMPOS = CAMPOS.flatMap((g) => g.items);
const CLAVES_DE_SETTINGS = TODOS_LOS_CAMPOS.filter((i) => !i.fueraDeSettings).map((i) => i.key);

// En la empresa los switches se mandan siempre: false es un valor y no un "no dijo nada".
//
// En el vehiculo NO. Antes tambien se mandaban siempre, copiando lo que la empresa tenia en ese
// momento, y esa copia le ganaba a la empresa para siempre: Mordelones prendio "marcar fuera de
// jornada" y su unico camion lo siguio teniendo apagado. Por eso en el vehiculo el switch tiene
// tres estados (como la empresa / Sí / No) y solo se guarda si se eligio Sí o No.
const SWITCHES = TODOS_LOS_CAMPOS.filter((i) => i.tipo === "switch").map((i) => i.key);

// Lo que el vehiculo puede pisar. El backend ignora el resto (Routing::Settings::VEHICLE_KEYS);
// no mandarlo evita guardar algo que no hace nada.
const CLAVES_DEL_VEHICULO = TODOS_LOS_CAMPOS
    .filter((i) => !i.fueraDeSettings && !i.soloEmpresa)
    .map((i) => i.key);

// El Select no se lleva bien con booleanos como value: se traducen a texto y de vuelta.
const HEREDA = "hereda";
const aOpcion = (v) => (v === true ? "si" : v === false ? "no" : HEREDA);
const deOpcion = (v) => (v === "si" ? true : v === "no" ? false : undefined);

const etiquetaBodega = (id, centres) => {
    const centre = (centres || []).find((c) => String(c.id) === String(id));
    if (!centre) return "Otra bodega";

    return centre.code ? `${centre.code} - ${centre.name}` : centre.name;
};

const formatearValor = (campo, valor, centres) => {
    if (campo.tipo === "switch") return valor ? "Sí" : "No";
    if (campo.tipo === "bodega") return etiquetaBodega(valor, centres);
    if (campo.tipo === "select") return campo.opciones.find((o) => o.value === valor)?.label || String(valor);
    if (campo.tipo === "number") return Number(valor).toLocaleString("es-CL");
    return String(valor);
};

// Lo que ESTE vehiculo hace distinto: solo las claves que pisa, en el orden del catalogo.
//
// No son los valores efectivos a proposito. El resumen de la empresa (`RoutingSettingsPanel`)
// muestra los efectivos, porque ahi la pregunta es "como rutea la empresa"; en la ficha del
// vehiculo la pregunta es "que tiene de particular este camion", y repetir los valores heredados
// la contesta peor que no mostrar nada.
export const resumenParticular = (vehicle, centres) => {
    const propios = vehicle?.routing_settings || {};

    // Sin lo soloEmpresa: el backend lo ignora en el vehiculo, y mostrarlo seria contar algo
    // que no pasa.
    return TODOS_LOS_CAMPOS
        .filter((campo) => !campo.soloEmpresa)
        .filter((campo) => (campo.fueraDeSettings
            ? vehicle?.[campo.key] === true
            : Object.prototype.hasOwnProperty.call(propios, campo.key)))
        .map((campo) => ({
            key: campo.key,
            label: campo.label,
            valor: formatearValor(campo, campo.fueraDeSettings ? vehicle?.[campo.key] : propios[campo.key], centres),
        }));
};

const Ayuda = ({ texto }) => (
    <Tooltip arrow title={texto}>
        <InfoOutlinedIcon fontSize="small" color="action" sx={{ ml: 0.5, cursor: "help", verticalAlign: "middle" }} />
    </Tooltip>
);

const RoutingSettingsDialog = ({ open, onClose, scope, target, inherited, onSaved }) => {
    const { enqueueSnackbar } = useSnackbar();
    const esVehiculo = scope === "vehicle";
    const [form, setForm] = useState({});
    const [centres, setCentres] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;

        // A nivel empresa los campos arrancan con lo que HOY se aplica: una empresa sin nada
        // configurado igual rutea de una forma, y abrir el dialogo en blanco obliga a saberse los
        // valores por defecto de memoria. Los efectivos los resuelve el backend.
        //
        // A nivel vehiculo arrancan con lo PROPIO, no con lo efectivo: si se precargara lo
        // heredado, guardar convertiria en override cada campo que el vehiculo solo estaba
        // heredando. Los switches tambien: el que no esta guardado arranca en "como la empresa".
        const propios = target?.routing_settings || {};
        const efectivos = target?.routing_settings_effective || propios;

        const inicial = esVehiculo ? { ...propios } : { ...efectivos };

        setForm({ ...inicial, avoid_tolls: target?.avoid_tolls === true });
    }, [open, target, esVehiculo]);

    useEffect(() => {
        if (!open) return;

        centreServices.searchByCompany()
            .then((data) => setCentres(Array.isArray(data) ? data : []))
            .catch(() => setCentres([]));
    }, [open]);

    const construirPayload = () => (esVehiculo ? CLAVES_DEL_VEHICULO : CLAVES_DE_SETTINGS).reduce((acc, k) => {
        const v = form[k];
        if (SWITCHES.includes(k) && !esVehiculo) return { ...acc, [k]: Boolean(v) };
        if (v === "" || v === undefined || v === null) return acc;

        return { ...acc, [k]: v };
    }, {});

    const save = async () => {
        try {
            setSaving(true);
            const routing_settings = construirPayload();

            if (esVehiculo) {
                await vehicleServices.saveVehicleRoutingSettings(target.id, {
                    vehicle: { routing_settings, avoid_tolls: form.avoid_tolls === true },
                });
            } else {
                await companyServices.saveCompanySettings(target.id, { routing_settings });
            }

            if (onSaved) onSaved();

            // El aviso va acá y no en un cartel dentro del formulario: importa en el momento en
            // que se guarda, no todo el rato que uno pasa configurando.
            await Swal.fire({
                icon: "success",
                title: "Configuración de ruteo guardada",
                text: "Los viajes que se calculen de aquí en adelante usan esta configuración. Los ya calculados no se tocan.",
                timer: 3000,
                showConfirmButton: false,
            });
            onClose();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.error || "No se pudo guardar", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    // Lo que la empresa dice para ese campo, para usarlo de placeholder: el campo en blanco del
    // vehiculo significa "lo que diga la empresa", y sin esto habria que ir a mirarlo.
    // Primero lo efectivo de la empresa, que ya trae resueltos los defaults: `inherited` es el
    // jsonb crudo y un switch que la empresa nunca toco salia como "sin definir".
    const deLaEmpresa = target?.company_routing_settings || inherited || {};

    const valorHeredado = (key) => {
        const v = deLaEmpresa[key];
        if (v === true) return "Sí";
        if (v === false) return "No";
        if (v === null || v === undefined || v === "") return "sin definir";

        return String(v);
    };

    // Lo que va a aplicar si se guarda asi: en el vehiculo, lo vacio es lo de la empresa. Es lo
    // que decide que campos mostrar —un "Vuelve a" depende de si el camion vuelve, lo diga el
    // o lo herede—.
    const aplicado = esVehiculo
        ? Object.keys(form).reduce((acc, k) => (
            form[k] === "" || form[k] === undefined || form[k] === null ? acc : { ...acc, [k]: form[k] }
        ), { ...deLaEmpresa })
        : form;

    const visible = (campo) => {
        if (campo.soloEmpresa && esVehiculo) return false;
        if (campo.soloVehiculo && !esVehiculo) return false;
        if (campo.dependeDe && !aplicado[campo.dependeDe]) return false;
        // `visibleSi` existe porque `dependeDe` solo sabe de switches: los campos
        // eléctricos dependen de un VALOR (energy_type), no de un booleano.
        if (campo.visibleSi && !campo.visibleSi(aplicado)) return false;

        return true;
    };

    const renderCampo = (campo) => {
        const valor = form[campo.key];
        const set = (v) => setForm({ ...form, [campo.key]: v });
        // La ayuda queda solo en la configuracion de la empresa. En la ficha del vehiculo son
        // cuatro campos y una i por campo era mas ruido que explicacion.
        const ayuda = campo.help && !esVehiculo ? <Ayuda texto={campo.help} /> : null;

        let control;
        if (campo.tipo === "switch" && esVehiculo && !campo.fueraDeSettings) {
            control = (
                <FormControl fullWidth size="small">
                    <InputLabel>{campo.label}</InputLabel>
                    <Select label={campo.label} value={aOpcion(valor)} onChange={(e) => set(deOpcion(e.target.value))}>
                        <MenuItem value={HEREDA}>{`Como la empresa (${valorHeredado(campo.key)})`}</MenuItem>
                        <MenuItem value="si">Sí</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                    </Select>
                </FormControl>
            );
        } else if (campo.tipo === "switch") {
            control = (
                <FormControlLabel
                    control={<Switch checked={Boolean(valor)} onChange={(e) => set(e.target.checked)} />}
                    label={<React.Fragment>{campo.label}{ayuda}</React.Fragment>}
                />
            );
        } else if (campo.tipo === "bodega") {
            control = (
                <FormControl fullWidth size="small">
                    <InputLabel>{campo.label}</InputLabel>
                    <Select label={campo.label} value={valor ?? ""} onChange={(e) => set(e.target.value)}>
                        <MenuItem value="">
                            {esVehiculo && deLaEmpresa[campo.key]
                                ? `Como la empresa (${etiquetaBodega(deLaEmpresa[campo.key], centres)})`
                                : "La bodega de salida"}
                        </MenuItem>
                        {centres.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                                {c.code ? `${c.code} - ${c.name}` : c.name}
                            </MenuItem>
                        ))}
                    </Select>
                    {ayuda && <FormHelperText sx={{ ml: 0 }}>{ayuda}</FormHelperText>}
                </FormControl>
            );
        } else if (campo.tipo === "select") {
            control = (
                <FormControl fullWidth size="small">
                    <InputLabel>{campo.label}</InputLabel>
                    <Select label={campo.label} value={valor ?? ""} onChange={(e) => set(e.target.value)}>
                        {campo.opciones.map((o) => (
                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                    </Select>
                    {ayuda && <FormHelperText sx={{ ml: 0 }}>{ayuda}</FormHelperText>}
                </FormControl>
            );
        } else {
            control = (
                <TextField
                    fullWidth size="small"
                    type={campo.tipo === "number" ? "number" : "text"}
                    label={campo.label}
                    placeholder={esVehiculo ? `Empresa: ${valorHeredado(campo.key)}` : campo.placeholder}
                    InputProps={{ endAdornment: ayuda }}
                    value={valor ?? ""} onChange={(e) => set(e.target.value)}
                />
            );
        }

        return (
            <Grid item xs={12} sm={6} key={campo.key}>
                {control}
            </Grid>
        );
    };

    const grupos = CAMPOS
        .map((grupo) => ({ ...grupo, items: grupo.items.filter(visible) }))
        .filter((grupo) => grupo.items.length > 0);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Typography variant={"h2"} color="primary">
                    Ruteo · {esVehiculo ? `Vehículo ${target?.code || ""}` : target?.name}
                </Typography>
            </DialogTitle>
            <DialogContent>
                {grupos.map((grupo, idx) => (
                    <React.Fragment key={grupo.grupo}>
                        {idx > 0 && <Divider sx={{ my: 2 }} />}
                        <Typography variant="subtitle2" color="primary" sx={{ mt: idx === 0 ? 1 : 0, mb: 1.5 }}>
                            {grupo.grupo}
                            {grupo.help && !esVehiculo && <Ayuda texto={grupo.help} />}
                        </Typography>
                        <Grid container spacing={2}>
                            {grupo.items.map(renderCampo)}
                        </Grid>
                        {grupo.grupo === "Energía" && costoCombustible(form) !== null && (
                            <Alert severity="success" sx={{ mt: 1.5, py: 0.5 }}>
                                <Typography variant="body2">
                                    {form.energy_type === "electric"
                                        ? `Costo por kilómetro (respaldo): $${costoCombustible(form).toLocaleString("es-CL")} · al rutear se usan los kWh que mide HERE`
                                        : `Costo por kilómetro: $${costoCombustible(form).toLocaleString("es-CL")}`}
                                </Typography>
                            </Alert>
                        )}
                        {grupo.grupo === "Chofer" && costoChofer(form) !== null && (
                            <Alert severity="success" sx={{ mt: 1.5, py: 0.5 }}>
                                <Typography variant="body2">
                                    {`Costo por hora: $${costoChofer(form).toLocaleString("es-CL")}`}
                                    {` · costo empresa mensual: $${Math.round(Number(form.driver_net_salary) * (Number(form.driver_cost_factor) || 1.4)).toLocaleString("es-CL")}`}
                                </Typography>
                            </Alert>
                        )}
                    </React.Fragment>
                ))}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cerrar</Button>
                <LoadingButton
                    loading={saving}
                    variant="contained"
                    color="success"
                    size="small"
                    endIcon={<SaveOutlinedIcon />}
                    loadingPosition="end"
                    onClick={save}>
                        {saving ? "Guardando" : "Guardar"}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default RoutingSettingsDialog;
